import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

// Context
import { useStock } from '../contexts/StockContext';
import { useLanguage } from '../contexts/LanguageContext';
import { cle, useMessageTraduit } from '../utils/messageTraduit';
import EnteteEcran from '../components/commun/EnteteEcran';
import BarreFiltres from '../components/commun/BarreFiltres';
import PiedTableau from '../components/commun/PiedTableau';
import PastilleStatut from '../components/commun/PastilleStatut';
import { useFormatageDates } from '../utils/dates';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  LocalShipping as LocalShippingIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
  Cancel as CancelIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

import { useLocation, useNavigate, useParams, useMatch } from 'react-router-dom';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { formatCurrency } from '../utils/currency';
import { numeroAffichable, raccourcisseurDeNumeros } from '../utils/numeroCommande';
import DialogueConfirmation from '../components/commun/DialogueConfirmation';
import { productService } from '../services/productService';
import { supplierService } from '../services/supplierService';
import PurchaseOrderFormWithRemises from '../components/PurchaseOrderFormWithRemises';
import DialogueDetailCommande from '../components/commandes/DialogueDetailCommande';
import BandeauPaniers from '../components/commandes/BandeauPaniers';
import { viderPanier } from '../utils/panier';
import { ORDER_STATUS, ORDER_STATUS_META, isCancellable, canReceive, isEditable } from '../constants/orderStatus';
import { derivePaymentStatus } from '../constants/paymentStatus';
import { canDelete, DELETABLE } from '../utils/permissions';



const getStatusOptions = (t) =>
  Object.entries(ORDER_STATUS_META).map(([value, meta]) => ({
    value,
    label: t(meta.i18nKey),
    color: meta.colorKey,
  }));

const PurchaseOrders = ({ showSnackbar, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshTrigger, currentStock, triggerRefresh } = useStock();
  const { t } = useLanguage();
  const { formaterDateSaisie } = useFormatageDates();
  
  // Initialiser le prevRefreshTrigger depuis sessionStorage pour éviter les rechargements au remontage
  const prevRefreshTriggerRef = React.useRef((() => {
    try {
      const saved = sessionStorage.getItem('purchaseOrders_lastRefreshTrigger');
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  })());
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useMessageTraduit();
  // `editingOrder` reste un état : c'est la commande CHARGÉE (avec ses lignes),
  // pas l'ouverture. L'ouverture, elle, vient de l'URL — un état qui porterait
  // les deux ferait de nouveau survivre le formulaire à la navigation.
  const [editingOrder, setEditingOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [parPage, setParPage] = useState(20);

  /**
   * 🔴 L'ANNONCE AVANT UN GESTE IRRÉVERSIBLE — deux gestes voisins que le
   * dialogue natif ne distinguait pas.
   *
   * **C'est ce défaut qui a fait annuler une commande par accident** : *un
   * dialogue natif se valide sans qu'on lise l'annonce* — il ne montre rien,
   * il bloque le fil, et il ne suit ni le thème ni la langue.
   *
   * ⚠️ ET LES DEUX GESTES SONT CÔTE À CÔTE DANS LA MÊME LIGNE : annuler garde
   * la trace, supprimer l'efface. *Deux boutons voisins aux effets opposés
   * exigent que l'annonce dise LEQUEL on vient de cliquer* — d'où le numéro de
   * commande dans le titre.
   */
  const [aConfirmer, setAConfirmer] = useState(null);
  const [confirmationEnCours, setConfirmationEnCours] = useState(false);

  const executerConfirmation = async () => {
    if (!aConfirmer?.executer) return;
    setConfirmationEnCours(true);
    try {
      await aConfirmer.executer();
    } finally {
      setConfirmationEnCours(false);
      setAConfirmer(null);
    }
  };

  const [statusFilter, setStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  // 🔴 L'OUVERTURE VIENT DE L'URL, PAS D'UN `useState`. `viewMode` remplaçait
  // la liste par le détail : « précédent » quittait donc l'écran au lieu de
  // refermer le détail, et le lien n'était pas partageable.
  const { id: commandeDetailleeId } = useParams();
  // 🔴 LE FORMULAIRE AUSSI DÉRIVE DE L'URL — `/purchase-orders/nouveau` et
  // `/purchase-orders/:id/modifier`. Même raison que pour le détail : sans ça,
  // « précédent » recule dans l'historique sans refermer le formulaire, et un
  // lien de création ne s'ouvre pas. `useMatch` répond sur le CHEMIN, ce que
  // `useParams` ne peut pas faire : `:id` vaut `undefined` sur `/nouveau`.
  const surCreation = Boolean(useMatch('/purchase-orders/nouveau'));
  const surEdition = useMatch('/purchase-orders/:id/modifier');
  const commandeEditeeId = surEdition?.params?.id;
  const [menuActions, setMenuActions] = useState({ ancre: null, orderId: null });
  // Le panier REPRIS, qui alimente le formulaire de création. Il n'est vidé
  // qu'après un 201 — cf. `onSubmit`.
  const [panierRepris, setPanierRepris] = useState(null);
  const loadingOrdersRef = React.useRef(false); // Pour éviter les appels simultanés
  const lastLoadTimeRef = React.useRef(0); // Pour éviter les appels trop rapprochés
  
  const isSuppliersProductsLoaded = () => {
    try {
      return sessionStorage.getItem('purchaseOrders_suppliersProductsLoaded') === 'true';
    } catch {
      return false;
    }
  };
  
  const setSuppliersProductsLoaded = (loaded) => {
    try {
      sessionStorage.setItem('purchaseOrders_suppliersProductsLoaded', loaded ? 'true' : 'false');
    } catch {}
  };

  // Charger les commandes (séparé des fournisseurs/produits)
  const loadOrders = useCallback(async () => {
    const now = Date.now();
    
    // Éviter les appels simultanés
    if (loadingOrdersRef.current) {
      return;
    }
    
    // Éviter les appels trop rapprochés (< 500ms)
    if (now - lastLoadTimeRef.current < 500) {
      return;
    }
    
    lastLoadTimeRef.current = now;

    try {
      loadingOrdersRef.current = true;
      setLoading(true);
      
      // 🔴 LES DEUX FILTRES DE STATUT PARTENT AU SERVEUR — ils découpaient la
      // PAGE, en JavaScript. Sur 12 commandes en deux pages, choisir « payé »
      // ne montrait que les payées DE LA PAGE COURANTE, et le pied annonçait
      // toujours 12. *Une limite qui vit dans un filtre client ne filtre que
      // ce qui est déjà arrivé* — leçon D2 de RU5-e, troisième occurrence.
      const params = {
        page,
        per_page: parPage,
        search: searchTerm,
        status: statusFilter || undefined,
        payment_status: paymentStatusFilter || undefined,
      };
      
      const ordersData = await purchaseOrderService.getPurchaseOrders(params);
      
      if (!ordersData || !ordersData.orders) {
        throw new Error('Format de réponse invalide');
      }
      
      setOrders(ordersData.orders);
      setTotalPages(ordersData.pages || 1);
      setTotal(ordersData.total ?? 0);

      // 🔴 LE SECOND APPEL A DISPARU. Le tableau demandait ici TOUS les paiements
      // du stock — `getAllPayments()`, sans pagination, quelle que soit la page
      // affichée — uniquement pour additionner de quoi dériver le statut de
      // paiement et connaître le reste dû. La liste sert désormais `total_paid`
      // et `remaining_amount` par ligne, calculés en une requête groupée côté
      // serveur. Un écran de 20 commandes ne rapatrie plus l'historique de
      // paiement d'une entreprise entière pour afficher cinq pastilles.
    } catch (err) {
      console.error('Erreur lors du chargement des commandes:', err);
      setError(cle('messages.loadingError'));
      if (showSnackbar) {
        showSnackbar(t('messages.loadingError'), 'error');
      }
    } finally {
      setLoading(false);
      loadingOrdersRef.current = false;
    }
  }, [page, parPage, searchTerm, statusFilter, paymentStatusFilter]); // showSnackbar retiré car recréé à chaque render

  // Charger les fournisseurs et produits une seule fois (avec cache persistant)
  useEffect(() => {
    if (isSuppliersProductsLoaded() && suppliers.length > 0 && products.length > 0) {
      return; // Déjà chargés
    }

    let cancelled = false;
    
    const loadSuppliersAndProducts = async () => {
      try {
        const [suppliersData, productsData] = await Promise.all([
          supplierService.getSuppliers({ per_page: 1000 }),
          productService.getProducts({ per_page: 1000 })
        ]);
        
        if (!cancelled) {
          setSuppliers(suppliersData.suppliers);
          setProducts(productsData.products);
          setSuppliersProductsLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Erreur lors du chargement des fournisseurs/produits:', err);
        }
      }
    };

    loadSuppliersAndProducts();

    return () => {
      cancelled = true;
    };
  }, [suppliers.length, products.length]); // Vérifier aussi si déjà chargés dans le state

  /**
   * 🔴 LA GARDE MANUELLE ANTI-REMONTAGE RENDAIT LE SÉLECTEUR « PAR PAGE »
   * TOTALEMENT INERTE — ET C'EST PIRE QUE LE DÉFAUT DU LOT P.
   *
   * L'ancienne écriture tenait une `ref` des valeurs précédentes et refusait de
   * recharger si elles n'avaient pas changé. Elle en connaissait **deux** :
   * `page` et `searchTerm`. Ses dépendances étaient `[page, searchTerm]`.
   * **`parPage` n'était NI dans les dépendances, NI dans la comparaison** :
   * changer « par page » ne déclenchait donc **aucune requête** — mesuré à
   * l'écran, zéro appel capturé sur `fetch` comme sur `XHR`.
   *
   * ⚠️ **PLUS SILENCIEUX QUE LE DÉFAUT DE `Products`** : là-bas la liste
   * clignotait — un rechargement partait, avec un `per_page` figé. *Ici rien ne
   * bouge du tout : le sélecteur affiche « 10 », la liste en montre 12, et
   * aucun signe ne dit que le réglage n'a pas été pris.*
   *
   * 🔴 CE QUI A ÉTÉ RETIRÉ, ET POURQUOI PLUTÔT QUE D'AJOUTER `parPage`.
   * Ajouter la clé manquante aurait refermé CE cas et laissé le mécanisme :
   * la prochaine valeur qui doit déclencher un rechargement — un filtre, un
   * tri — serait oubliée de la même façon, et **le même silence recommencerait**.
   * *Une liste de clés tenue à la main est une liste qu'on oublie de tenir* —
   * on en a vu six cette semaine. `useCallback` fait déjà ce travail : il
   * change d'identité **exactement** quand l'une de ses dépendances change,
   * sans qu'on ait à les énumérer une seconde fois.
   *
   * ⚠️ ET LA PROTECTION D'ORIGINE N'EST PAS PERDUE : React n'exécute un effet
   * que si une dépendance a changé par **identité**. `loadOrders` étant un
   * `useCallback`, un remontage sans changement d'état ne le recrée pas — c'est
   * la garde que la `ref` réécrivait à la main, en moins fiable.
   */
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Effet pour recharger les commandes quand le stock change
  useEffect(() => {
    // Ne se déclencher que si refreshTrigger a vraiment changé et est > 0
    if (refreshTrigger > 0 && refreshTrigger !== prevRefreshTriggerRef.current) {
      prevRefreshTriggerRef.current = refreshTrigger;
      // Sauvegarder dans sessionStorage pour persister au remontage
      try {
        sessionStorage.setItem('purchaseOrders_lastRefreshTrigger', refreshTrigger.toString());
      } catch {}
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  useEffect(() => {
    if (location.state?.viewOrderId) {
      // On n'attend plus que la liste soit chargée : l'URL suffit à ouvrir le
      // détail, qui va chercher la commande lui-même. L'ancienne version
      // cherchait la commande DANS la page courante — elle échouait donc
      // silencieusement si elle était sur une autre page de la pagination.
      navigate(`/purchase-orders/${location.state.viewOrderId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.viewOrderId, orders.length]);

  const handleOpenDialog = (order = null) =>
    navigate(order ? `/purchase-orders/${order.id}/modifier` : '/purchase-orders/nouveau');

  const handleCloseDialog = () => navigate('/purchase-orders');

  // La commande à modifier est chargée depuis l'URL, avec ses LIGNES — la liste
  // ne les sert pas. C'est ce qui fait qu'un lien `/…/12/modifier` collé dans un
  // onglet neuf ouvre un formulaire rempli, et non un formulaire vide.
  useEffect(() => {
    if (!commandeEditeeId) { setEditingOrder(null); return; }
    let annule = false;
    purchaseOrderService.getPurchaseOrderById(commandeEditeeId)
      .then((commande) => { if (!annule) setEditingOrder(commande); })
      .catch(() => { if (!annule) { showSnackbar(t('messages.detailsLoadError'), 'error'); navigate('/purchase-orders'); } });
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commandeEditeeId]);

  // 🔴 DEUX GESTES SÉPARÉS, ET ILS LE RESTENT JUSQU'AU BOUT. Le serveur les
  // distingue depuis longtemps — `create_purchase_order` ne pose que
  // `en_attente_envoi`, seuls `send-email` et `mark-sent` écrivent `envoye` ;
  // c'était le front qui les enchaînait en un clic.
  // ⚠️ ET SI L'ENVOI ÉCHOUE, LA COMMANDE EXISTE. On le DIT plutôt que de laisser
  // croire que rien n'a été fait : l'erreur porte sur l'envoi, pas sur la
  // création, et la commande est là, à renvoyer. C'est le même arbitrage que
  // l'invitation non obligatoire à la création d'une entreprise — deux gestes
  // séparés et réversibles valent mieux qu'un chemin où un échec laisse un
  // objet à moitié créé.
  const onSubmit = async (data, { envoyer = false } = {}) => {
    try {
      let commande;
      if (editingOrder) {
        commande = await purchaseOrderService.updatePurchaseOrder(editingOrder.id, data);
        showSnackbar(t('messages.orderUpdated'), 'success');
      } else {
        commande = await purchaseOrderService.createPurchaseOrder(data);
        showSnackbar(t('messages.orderCreated'), 'success');
      }
      // 🔴 LE PANIER N'EST VIDÉ QU'APRÈS UN 201, ET C'EST LE POINT LE PLUS
      // IMPORTANT DU LOT. Si la création échoue — un 400 nommant des lignes
      // devenues invalides, par exemple — le panier doit SURVIVRE : c'est tout
      // ce qui reste de la saisie. Le vider avant, ou en même temps, ferait
      // perdre le travail précisément dans le cas où on en a besoin.
      // ⚠️ Ce `if` est APRÈS les `await` de création : une exception saute
      // directement au `catch`, donc le panier reste — par construction, pas
      // par précaution.
      if (panierRepris && !editingOrder) {
        viderPanier(currentStock?.id, panierRepris.supplier_id);
        setPanierRepris(null);
      }
      if (envoyer && commande?.id) {
        try {
          const res = await purchaseOrderService.sendPurchaseOrderEmail(commande.id);
          showSnackbar(res.sent ? t('messages.orderSentByEmail')
                                : t('orders.emailSendFailed'),
                       res.sent ? 'success' : 'error');
        } catch (err) {
          showSnackbar(t('orders.emailSendFailed'), 'error');
        }
      }
      handleCloseDialog();
      loadOrders();
      triggerRefresh(); // Rafraîchir le dashboard
    } catch (err) {
      showSnackbar(err.response?.data?.error || t('messages.operationError'), 'error');
    }
  };

  const handleDelete = (order) => {
    setAConfirmer({
      type: 'suppression',
      numero: numeroAffichable(order.order_number) || `#${order.id}`,
      // 🔴 CE QUE LA SUPPRESSION EMPORTE, ET C'EST DÉFINITIF : la commande, ses
      // lignes, ses reçus et ses paiements. *L'annulation garde la trace ; la
      // suppression l'efface* — c'est toute la différence entre les deux
      // boutons voisins, et un `window.confirm` ne la disait pas.
      consequences: t('orders.deleteOrderConsequence', {
        lignes: (order.items_count ?? order.items?.length ?? 0),
      }),
      avertissement: t('orders.deleteOrderWarning'),
      executer: () => confirmDelete(order.id),
    });
  };

  const confirmDelete = async (orderId) => {
    try {
      await purchaseOrderService.deletePurchaseOrder(orderId);
      showSnackbar(t('messages.orderDeleted'), 'success');
      loadOrders();
      triggerRefresh(); // Rafraîchir le dashboard
    } catch (err) {
      showSnackbar(err.response?.data?.error || t('messages.deleteError'), 'error');
    }
  };

  // ⚠️ Le rendu passe par `PastilleStatut`, seul endroit du dépôt qui peint un
  // statut de commande. `getStatusOptions` reste — il sert au FILTRE, qui a
  // besoin de la liste des valeurs, pas de leur rendu.
  const getStatusChip = (status) => <PastilleStatut statut={status} />;

  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.company_name : 'Fournisseur inconnu';
  };

  const getPaymentStatusForOrder = (order) => {
    // Axe paiement dérivé : VIDE tant que la commande n'est pas livrée (avant livraison,
    // aucun paiement possible — cf. garde backend 409). La colonne reste vide.
    if (order.status !== ORDER_STATUS.LIVRE) {
      return { status: null, label: '', color: 'default' };
    }
    // 🔴 LE TOTAL PAYÉ VIENT DU SERVEUR. Il était additionné ici à partir d'une
    // carte de paiements chargée à part ; c'est l'agrégat groupé de la liste qui
    // le sert maintenant, ligne par ligne. Le tableau n'additionne plus rien.
    const totalPaid = parseFloat(order.total_paid) || 0;
    // ⚠️ NE REND PLUS QUE LE CODE. Le libellé et la couleur étaient fabriqués
    // ici ; ils appartiennent au RENDU, donc à `PastilleStatut`. Cette fonction
    // ne sert plus qu'au filtre et à la cellule, qui n'ont besoin que du code.
    return {
      status: derivePaymentStatus(
        parseFloat(order.total_payable || order.total_final) || 0, totalPaid),
    };
  };

  // ── LES ACTIONS D'UNE LIGNE, DANS L'ORDRE DE LEUR PERTINENCE ──────────────
  // 🔴 L'ORDRE EST LA SEULE CHOSE QUI COMPTE ICI, et c'est ce qui remplace la
  // rangée de six icônes. Le tableau rend les DEUX PREMIÈRES en clair et pousse
  // le reste sous « ⋯ » — donc ce qui est en tête doit être ce qu'on vient
  // faire à ce statut-là : envoyer une commande en attente d'envoi,
  // réceptionner une commande envoyée, relire les reçus d'une commande livrée.
  // Une liste plate ordonnée par HASARD d'écriture aurait mis « Supprimer » en
  // première position sur les commandes annulées.
  const actionsDeLaCommande = (order) => {
    const actions = [];
    if (order.status === ORDER_STATUS.EN_ATTENTE_ENVOI) {
      actions.push({ cle: 'envoyer', libelle: t('orders.sendEmail'), icone: <SendIcon fontSize="small" />,
                     couleur: 'primary', action: () => handleSendEmail(order.id) });
    }
    if (canReceive(order.status)) {
      actions.push({ cle: 'receptionner', libelle: t('orders.reception'), icone: <LocalShippingIcon fontSize="small" />,
                     couleur: 'success', action: () => handleReception(order) });
    }
    if (isEditable(order.status)) {
      actions.push({ cle: 'modifier', libelle: t('orders.edit'), icone: <EditIcon fontSize="small" />,
                     couleur: 'default', action: () => handleOpenDialog(order) });
    }
    actions.push({ cle: 'recus', libelle: t('orders.viewReceipts'), icone: <ReceiptIcon fontSize="small" />,
                   couleur: 'secondary', action: () => handleViewReceipts(order) });
    if (isCancellable(order.status)) {
      actions.push({ cle: 'annuler', libelle: t('common.cancel'), icone: <CancelIcon fontSize="small" />,
                     couleur: 'warning', action: () => handleCancel(order) });
    }
    if (canDelete(user, DELETABLE.ORDERS)) {
      actions.push({ cle: 'supprimer', libelle: t('orders.delete'), icone: <DeleteIcon fontSize="small" />,
                     couleur: 'error', action: () => handleDelete(order) });
    }
    return actions;
  };

  const ACTIONS_EN_CLAIR = 2;

  // Calculé une fois pour la page : les segments à élider dépendent de
  // l'ENSEMBLE des numéros affichés, pas de chacun pris à part.
  const raccourcirNumero = raccourcisseurDeNumeros((orders || []).map((o) => o.order_number));

  const ouvrirMenuActions = (e, orderId) => {
    e.stopPropagation();
    setMenuActions({ ancre: e.currentTarget, orderId });
  };
  const fermerMenuActions = () => setMenuActions({ ancre: null, orderId: null });



  const handleSendEmail = async (orderId) => {
    try {
      const res = await purchaseOrderService.sendPurchaseOrderEmail(orderId);
      if (res.sent) {
        showSnackbar(t('messages.orderSentByEmail'), 'success');
      } else {
        showSnackbar(t('orders.emailSendFailed'), 'error');
      }
      loadOrders(); // Recharger les données pour mettre à jour le statut
    } catch (error) {
      showSnackbar(t('messages.orderSendError'), 'error');
    }
  };

  const handleReception = (order) => {
    navigate('/order-reception', { state: { selectedOrderId: order.id } });
  };

  const handleViewReceipts = (order) => navigate(`/purchase-orders/${order.id}`);

  const handleCloseReceiptsPanel = () => navigate('/purchase-orders');

  const handleCancel = async (order) => {
    setAConfirmer({
      type: 'annulation',
      numero: numeroAffichable(order.order_number) || `#${order.id}`,
      // ⚠️ CE QU'ELLE NE FAIT PAS EST AUSSI IMPORTANT : la route pose
      // `status = ANNULE` et **ne touche ni au stock ni aux reçus**. La
      // commande RESTE dans la liste — c'est une trace, pas un déchet.
      consequences: t('orders.cancelConsequence', {
        lignes: (order.items_count ?? order.items?.length ?? 0),
      }),
      executer: () => executerAnnulation(order),
    });
  };

  const executerAnnulation = async (order) => {
    try {
      await purchaseOrderService.cancelOrder(order.id);
      showSnackbar(t('orders.orderCancelled'), 'success');
      loadOrders();
    } catch (error) {
      showSnackbar(t('orders.cancelError'), 'error');
    }
  };

  // Le dialogue recharge la commande ; l'écran ne remet à jour que sa liste.
  const handleOrderUpdated = () => loadOrders();

  if (loading && (!orders || orders.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  /**
   * 🔴 TOUT FILTRE REMET À LA PAGE 1 — ET DANS LE MÊME GESTE QUE LE FILTRE.
   *
   * Le lot P′ a retiré une garde manuelle anti-remontage qui rendait le
   * sélecteur « par page » inerte. **Elle portait DEUX propriétés, et je n'en
   * avais énuméré qu'une** : elle évitait les rechargements inutiles *(le
   * défaut corrigé)*, et **elle ramenait à la page 1 quand le filtre changeait**
   * *(la propriété perdue)*. Mesuré à l'écran par le propriétaire : en page 2,
   * taper une recherche laissait « page 2 » — et effacer la recherche ne
   * revenait pas non plus. **Il fallait cliquer la page 1 à la main.**
   *
   * ⚠️ *Avant de retirer un mécanisme, énumérer ce qu'il TIENT, pas seulement
   * ce qu'il casse.*
   *
   * 🔴 ET LA REMISE VIT DANS LE GESTIONNAIRE, PAS DANS UN EFFET. Un effet
   * `useEffect(() => setPage(1), [searchTerm])` corrigerait APRÈS coup : le
   * rendu intermédiaire partirait avec `page = 2` et le nouveau terme, donc une
   * requête pour rien — et, sur une liste courte, un aller-retour visible.
   * *Un état dérivé d'un autre ne se corrige pas après coup : il s'écrit dans
   * le même geste.*
   */
  const filtrer = (appliquer) => (valeur) => {
    appliquer(valeur);
    setPage(1);
  };

  return (
    <Box sx={{ ml: 0, mr: 0, pl: 0, pr: 0 }}>
      <EnteteEcran
        titre={t('orders.title')}
        compteur={total || undefined}
      >
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          {t('orders.newOrder')}
        </Button>
      </EnteteEcran>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DialogueDetailCommande
        key={commandeDetailleeId}
        id={commandeDetailleeId}
        ouvert={Boolean(commandeDetailleeId)}
        onFermer={handleCloseReceiptsPanel}
        contexte={t('orders.title')}
        showSnackbar={showSnackbar}
        onCommandeModifiee={handleOrderUpdated}
      />

      <BandeauPaniers
        produits={products}
        fournisseurs={suppliers}
        stockId={currentStock?.id}
        onReprendre={(panier) => {
          setPanierRepris(panier);
          navigate('/purchase-orders/nouveau');
        }}
      />

      {(
        <>
          <BarreFiltres
            valeur={searchTerm}
            onChange={filtrer(setSearchTerm)}
            libelle={t('orders.search')}
          >
            <TextField
              select
              size="small"
              label={t('orders.status')}
              value={statusFilter}
              onChange={(e) => filtrer(setStatusFilter)(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">{t('products.allStocks')}</MenuItem>
              {/* ⚠️ `getStatusOptions` sert au FILTRE — il fournit la liste des
                  valeurs. Le RENDU d'un statut, lui, passe uniquement par
                  `PastilleStatut`. Deux besoins distincts, deux chemins. */}
              {getStatusOptions(t).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('orders.paymentStatus')}
              value={paymentStatusFilter}
              onChange={(e) => filtrer(setPaymentStatusFilter)(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">{t('products.allStocks')}</MenuItem>
              <MenuItem value="pending">{t('orders.paymentStatusPending')}</MenuItem>
              <MenuItem value="partial">{t('orders.paymentStatusPartial')}</MenuItem>
              <MenuItem value="paid">{t('orders.paymentStatusPaid')}</MenuItem>
            </TextField>
          </BarreFiltres>

      {/* Liste des commandes */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          ml: 0, 
          mr: 0,
          '& .MuiPaper-root': {
            padding: 0,
            margin: 0,
          }
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {/* 🔴 CINQ COLONNES AU LIEU DE SEPT. « Date » et « Statut de
                  paiement » ne disparaissent pas : elles rejoignent la colonne
                  à laquelle elles APPARTIENNENT. Une date de commande sous son
                  numéro se lit comme une seule information ; en colonne, elle
                  demandait une largeur pour un contenu qu'on ne compare jamais
                  d'une ligne à l'autre. */}
              <TableCell>{t('orders.orderNumber')}</TableCell>
              <TableCell>{t('orders.supplier')}</TableCell>
              <TableCell>{t('orders.status')}</TableCell>
              {/* 🔴 « Total HT » ET « Remises » SONT RETIRÉES. Elles disaient comment
                  le total a été construit, pas ce qu'il vaut — une décomposition
                  qui a sa place dans le détail de la commande, où elle est déjà
                  servie ligne à ligne, et qui coûtait ici deux colonnes sur neuf
                  pour un chiffre que personne ne compare d'une ligne à l'autre. */}
              <TableCell>{t('orders.finalTotal')}</TableCell>
              <TableCell align="right">{t('products.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* 🔴 LE FILTRAGE CLIENT EST RETIRÉ — le serveur filtre désormais la
                COLLECTION. Le laisser ferait refiltrer ce qui l'est déjà : sans
                effet visible aujourd'hui, mais **une seconde écriture de la
                règle**, qui divergerait le jour où l'une des deux changerait.
                *Deux définitions d'un même filtre finissent par montrer deux
                listes différentes.* */}
            {(orders || []).map((order) => (
              <TableRow key={order.id} hover sx={{
                cursor: 'pointer',
                // 🔴 UNE COMMANDE ANNULÉE EST GRISÉE ET BARRÉE. Elle reste dans la
                // liste — c'est une trace, pas un déchet — mais elle ne doit pas se
                // lire comme une commande vivante. Le barré porte sur les
                // TYPOGRAPHIES et pas sur la ligne entière : appliqué au `<tr>`, il
                // traverserait aussi les pastilles de statut, où un libellé
                // « Annulée » rayé devient illisible.
                ...(order.status === ORDER_STATUS.ANNULE && {
                  opacity: 0.55,
                  '& .MuiTypography-root': { textDecoration: 'line-through' },
                }),
              }} onClick={e => {
                // Ne pas ouvrir si clic sur une icône d'action
                if (e.target.closest('button')) return;
                // 🔴 LE CLIC N'OUVRE LE FORMULAIRE QUE SI LA COMMANDE EST MODIFIABLE.
                // Il l'ouvrait sur N'IMPORTE QUELLE ligne : sur une commande livrée,
                // on obtenait un formulaire d'apparence normale, on saisissait, et le
                // serveur refusait en 400 — il ne laisse modifier qu'une commande en
                // attente d'envoi. *Un écran qui laisse faire un geste dont il ne
                // montre pas la conséquence.* Ailleurs, la ligne mène au DÉTAIL,
                // comme sur l'écran Produits.
                if (isEditable(order.status)) handleOpenDialog(order);
                else handleViewReceipts(order);
              }}>
                <TableCell>
                  {/* 🔴 LE NUMÉRO EST RACCOURCI DE SES SEGMENTS COMMUNS, et il
                      reste ENTIER dans le détail, le PDF et l'e-mail — c'est une
                      référence externe. Le titre porte le numéro complet : la
                      valeur reste accessible sans quitter la ligne. */}
                  <Typography variant="subtitle2" fontWeight="bold"
                              title={order.order_number || ''}>
                    {raccourcirNumero(order.order_number) || `#${order.id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formaterDateSaisie(order.order_date)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {getSupplierName(order.supplier_id)}
                </TableCell>
                <TableCell>
                  {/* 🔴 LES DEUX AXES DANS LA MÊME COLONNE, l'un sous l'autre.
                      Ils décrivent le même objet à deux moments — la commande
                      avance, puis elle se paie — et leur voisinage en colonnes
                      les faisait lire comme deux propriétés indépendantes. */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                    {getStatusChip(order.status)}
                  {/* 🔴 CETTE COLONNE ÉTAIT RESTÉE UN `<Chip>` alors que sa
                      VOISINE IMMÉDIATE était passée en pastille : deux
                      traitements dans le même tableau, à une colonne d'écart.
                      La garde ne l'avait pas vu parce qu'elle ne surveillait
                      qu'un seul jeu de constantes — `ORDER_STATUS_META` — et
                      que le paiement a le sien. */}
                    {getPaymentStatusForOrder(order).status && (
                      <PastilleStatut
                        statut={getPaymentStatusForOrder(order).status}
                        famille="paiement"
                      />
                    )}
                  {/* 🔴 LE RESTE DÛ SE LIT SOUS LA PASTILLE, SANS COLONNE À LUI.
                      Il n'apparaît que sur une commande LIVRÉE : avant livraison
                      aucun paiement n'est possible (garde serveur 409), donc
                      afficher « Restant 1 000 » sur une commande envoyée
                      annoncerait une dette qui n'est pas encore née. Et il ne
                      s'affiche pas à zéro — une commande soldée porte déjà sa
                      pastille « Payé », le répéter en chiffres n'ajoute rien. */}
                    {getPaymentStatusForOrder(order).status && order.remaining_amount > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {t('orders.remaining')} {formatCurrency(order.remaining_amount, order.currency || 'EUR')}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                      {(order.total_payable || order.total_final)
                        ? formatCurrency(order.total_payable || order.total_final, order.currency || 'EUR')
                        : 'N/A'}
                    </Typography>
                    {order.vat_enabled && (
                      <Chip
                        label={t('orders.vatBadge')}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {actionsDeLaCommande(order).slice(0, ACTIONS_EN_CLAIR).map((a) => (
                    <IconButton
                      key={a.cle}
                      size="small"
                      color={a.couleur}
                      onClick={(e) => { e.stopPropagation(); a.action(); }}
                      title={a.libelle}
                    >
                      {a.icone}
                    </IconButton>
                  ))}
                  {actionsDeLaCommande(order).length > ACTIONS_EN_CLAIR && (
                    <IconButton
                      size="small"
                      onClick={(e) => ouvrirMenuActions(e, order.id)}
                      title={t('orders.moreActions')}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Le menu des actions reléguées. Il est monté UNE fois pour le tableau et
          non une fois par ligne : vingt menus fermés, c'est vingt sous-arbres
          rendus pour rien. Le contenu est reconstruit à l'ouverture depuis la
          commande visée, donc il suit toujours le statut courant. */}
      <Menu
        anchorEl={menuActions.ancre}
        open={Boolean(menuActions.ancre)}
        onClose={fermerMenuActions}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {actionsDeLaCommande(
          (orders || []).find(o => o.id === menuActions.orderId) || {}
        ).slice(ACTIONS_EN_CLAIR).map((a) => (
          <MenuItem
            key={a.cle}
            onClick={() => { fermerMenuActions(); a.action(); }}
          >
            <ListItemIcon sx={{ color: a.couleur === 'default' ? 'inherit' : `${a.couleur}.main` }}>
              {a.icone}
            </ListItemIcon>
            <ListItemText>{a.libelle}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* 🔴 UNE ANNONCE POUR DEUX GESTES OPPOSÉS — et le NUMÉRO de commande
          figure dans le titre. Les deux boutons sont voisins dans la même
          ligne : *une annonce qui ne nomme pas son objet laisse le doute sur
          lequel on vient de cliquer*, et c'est exactement ainsi qu'une
          commande a été annulée par accident. */}
      <DialogueConfirmation
        ouvert={Boolean(aConfirmer)}
        titre={aConfirmer ? t(aConfirmer.type === 'annulation'
          ? 'orders.confirmCancelTitle' : 'orders.confirmDeleteOrderTitle',
        ) : ''}
        question={aConfirmer ? t(aConfirmer.type === 'annulation'
          ? 'orders.confirmCancelQuestionNum' : 'orders.confirmDeleteOrderQuestion',
          { numero: aConfirmer.numero }) : ''}
        consequences={aConfirmer?.consequences}
        avertissement={aConfirmer?.avertissement}
        libelleConfirmer={aConfirmer ? t(aConfirmer.type === 'annulation'
          ? 'orders.cancelOrder' : 'common.delete') : ''}
        enCours={confirmationEnCours}
        onConfirmer={executerConfirmation}
        onFermer={() => setAConfirmer(null)}
      />

      <PiedTableau
        total={total}
        page={page}
        pages={totalPages}
        parPage={parPage}
        onPage={setPage}
        onParPage={(n) => { setParPage(n); setPage(1); }}
        libelleTotal={`${total} ${t('orders.title').toLowerCase()}`}
        libelleParPage={t('common.perPage')}
      />
        </>
      )}

      {/* Nouveau formulaire avec remises */}
      <PurchaseOrderFormWithRemises
        open={surCreation || Boolean(commandeEditeeId)}
        onClose={handleCloseDialog}
        onSubmit={onSubmit}
        editingOrder={editingOrder}
        panierInitial={panierRepris}
        suppliers={suppliers}
        products={products}
        showSnackbar={showSnackbar}
      />


    </Box>
  );
};

export default PurchaseOrders; 